/**
 * Reusable `ContractDashboard` check builders.
 *
 * Every check file in this directory was reimplementing the same handful
 * of patterns inline: "two id sets agree", "every enum value is unique",
 * "caps are positive + finite", "a constant matches a regex". Factored
 * out so new check categories can compose these instead of writing 15
 * LOC of boilerplate per check.
 *
 * These helpers are pure functions that return a `Check` — caller is
 * responsible for pushing into the category's check list. Keeping the
 * composition explicit (vs. a fluent builder) keeps the per-check site
 * scannable.
 */
import type { Check, Status } from '../../DashboardShared';

/**
 * Shallow set-equality for two id lists. Sorts both and JSON-compares —
 * safe for small registry lists (<1000 entries), which is all we use.
 */
function sameIds(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
  return true;
}

/**
 * Verify that two id lists (derived from different sources of truth)
 * contain exactly the same ids. Use this any time you keep two
 * registries in sync — e.g. an enum + a definitions array, a map
 * registry + a derived ids array.
 */
export function registryAgreementCheck(args: {
  id: string;
  label: string;
  left: { name: string; ids: readonly string[] };
  right: { name: string; ids: readonly string[] };
}): Check {
  const { id, label, left, right } = args;
  const match = sameIds(left.ids, right.ids);
  return {
    id,
    label,
    status: match ? 'pass' : 'fail',
    detail: match
      ? `${left.ids.length} entries, ${left.name} ≡ ${right.name}`
      : `MISMATCH — ${left.name}=[${[...left.ids].sort().join(', ')}] ${right.name}=[${[...right.ids].sort().join(', ')}]`,
  };
}

/**
 * Verify every value in an enum-like record is unique.
 */
export function enumUniqueCheck(args: {
  id: string;
  label: string;
  record: Record<string, string>;
}): Check {
  const { id, label, record } = args;
  const values = Object.values(record);
  const unique = new Set(values);
  const ok = values.length === unique.size;
  return {
    id,
    label,
    status: ok ? 'pass' : 'fail',
    detail: ok
      ? `${values.length} unique ids`
      : `Duplicates present (${values.length} entries, ${unique.size} unique)`,
  };
}

/**
 * Verify a set of named numeric caps are all finite and satisfy a
 * per-cap predicate. Useful for "cooldown ≥ 0, caps ≥ 1" style checks.
 */
export function capsSanityCheck(args: {
  id: string;
  label: string;
  caps: Record<string, { value: number; ok: (v: number) => boolean }>;
}): Check {
  const { id, label, caps } = args;
  const bad: string[] = [];
  const parts: string[] = [];
  for (const [name, { value, ok }] of Object.entries(caps)) {
    parts.push(`${name}=${value}`);
    if (!Number.isFinite(value) || !ok(value)) bad.push(name);
  }
  return {
    id,
    label,
    status: bad.length === 0 ? 'pass' : 'fail',
    detail: bad.length === 0 ? parts.join(', ') : `Invalid: ${bad.join(', ')} (${parts.join(', ')})`,
  };
}

/**
 * Verify a string constant matches a regex — typical for message-name
 * conventions ("lobby_tool"), enum key shapes, channel names, etc.
 */
export function stringPatternCheck(args: {
  id: string;
  label: string;
  value: unknown;
  pattern: RegExp;
}): Check {
  const { id, label, value, pattern } = args;
  const ok = typeof value === 'string' && pattern.test(value);
  return {
    id,
    label,
    status: ok ? 'pass' : 'fail',
    detail: `"${typeof value === 'string' ? value : String(value)}"`,
  };
}

/**
 * Verify a boolean-able flag is readable (typeof === 'boolean'). Useful
 * for `DevConfig.foo` feature-flag presence checks.
 */
export function booleanFlagCheck(args: {
  id: string;
  label: string;
  flagName: string;
  value: unknown;
  overrideHint?: string;
}): Check {
  const { id, label, flagName, value, overrideHint } = args;
  const present = typeof value === 'boolean';
  const overridePart = overrideHint ? ` (override with ${overrideHint})` : '';
  return {
    id,
    label,
    status: present ? 'pass' : 'fail',
    detail: present ? `${value ? 'enabled' : 'disabled'}${overridePart}` : `missing: ${flagName}`,
  };
}

/**
 * Tag a status based on a per-row drift count. Returns `pass` when drift
 * is 0, `warn` for documentation-only drift (registry is still
 * authoritative), `fail` when drift breaks an invariant.
 */
export function driftStatus(drift: number, breaksInvariant = false): Status {
  if (drift === 0) return 'pass';
  return breaksInvariant ? 'fail' : 'warn';
}
